import { backendServices, RecursivePartial, DeliveryMethod, BusinessDocType, ProfileType, MessageType } from 'common';
import {
    ManualDataFeedFormat,
    FeedMessageType,
    FileTypeId,
    InvoiceProfileInfinx,
    ProfileExtendedData,
    IDE_THRESHOLD_MAX,
} from '../../constants';
import { BuyerSignupFormData } from '../../pages/Buyer/buyersignup.types';

function createContactsAndUsers(form: BuyerSignupFormData): {
    contacts: RecursivePartial<backendServices.ViewModels.ContactViewModel>[];
    users: RecursivePartial<backendServices.ViewModels.UserViewModel>[];
} {
    // set up company contacts and users
    const contacts = [
        {
            Name: form.generalInformation.contactSales.name,
            Phone: form.generalInformation.contactSales.phone,
            Email: form.generalInformation.contactSales.email,
            Type: 4,
            Type_US: 'Sales',
            Address1: form.generalInformation.buyerCompanyAddress.addressLine1,
            Address2: form.generalInformation.buyerCompanyAddress.addressLine2,
            Address3: form.generalInformation.buyerCompanyAddress.addressLine3,
            City: form.generalInformation.buyerCompanyAddress.city,
            State: form.generalInformation.buyerCompanyAddress.state,
            PostalCode: form.generalInformation.buyerCompanyAddress.zipCode,
            Country: form.generalInformation.buyerCompanyAddress.country,
        },
    ];
    const users = [
        {
            Name: form.generalInformation.contactSales.name,
            Email: form.generalInformation.contactSales.email,
            Phone: form.generalInformation.contactSales.phone,
        },
    ];
    if (!form.generalInformation.contactAdministrator.isSameAsSalesContact) {
        contacts.push({
            Name: form.generalInformation.contactAdministrator.name,
            Phone: form.generalInformation.contactAdministrator.phone,
            Email: form.generalInformation.contactAdministrator.email,
            Type: 1,
            Type_US: 'Primary Support Contact',
            Address1: form.generalInformation.buyerCompanyAddress.addressLine1,
            Address2: form.generalInformation.buyerCompanyAddress.addressLine2,
            Address3: form.generalInformation.buyerCompanyAddress.addressLine3,
            City: form.generalInformation.buyerCompanyAddress.city,
            State: form.generalInformation.buyerCompanyAddress.state,
            PostalCode: form.generalInformation.buyerCompanyAddress.zipCode,
            Country: form.generalInformation.buyerCompanyAddress.country,
        });
        users.push({
            Name: form.generalInformation.contactAdministrator.name,
            Email: form.generalInformation.contactAdministrator.email,
            Phone: form.generalInformation.contactAdministrator.phone,
        });
    }

    return { contacts, users };
}

function createFTPInbound(
    form: BuyerSignupFormData
): RecursivePartial<backendServices.ViewModels.SharedFTPDownloadViewModel> {
    if (form.inboundDataFeeds.deliveryMethod.useEmail) {
        return {};
    }

    const ftpInbound = {
        FTPDownload: {
            Server: form.inboundDataFeeds.deliveryMethod.sftpConnection?.server ?? '',
            User: form.inboundDataFeeds.deliveryMethod.sftpConnection?.user ?? '',
            Password: form.inboundDataFeeds.deliveryMethod.sftpConnection?.password ?? '',
            DownloadFolder: form.inboundDataFeeds.deliveryMethod.sftpConnection?.downloadFolder ?? '',
            UploadFolder: form.inboundDataFeeds.deliveryMethod.sftpConnection?.uploadFolder ?? '',
            Protocol: Number(form.inboundDataFeeds.deliveryMethod.sftpConnection?.protocol),
            Port: Number(form.inboundDataFeeds.deliveryMethod.sftpConnection?.port),
            Timeout: Number(form.inboundDataFeeds.deliveryMethod.sftpConnection?.timeoutInSeconds),
        },
    };

    return ftpInbound;
}

function createFTPInfinx(
    form: BuyerSignupFormData
): RecursivePartial<backendServices.ViewModels.SharedFTPDownloadViewModel> {
    if (!form.inboundDataFeeds.infinxIntegration.invoiceProfileId) {
        return {};
    }

    const ftpInfinx = {
        FTPDownload: {
            Server: form.inboundDataFeeds.infinxIntegration.sftpConnection?.server ?? '',
            User: form.inboundDataFeeds.infinxIntegration.sftpConnection?.user ?? '',
            Password: form.inboundDataFeeds.infinxIntegration.sftpConnection?.password ?? '',
            DownloadFolder: form.inboundDataFeeds.infinxIntegration.sftpConnection?.downloadFolder ?? '',
            UploadFolder: form.inboundDataFeeds.infinxIntegration.sftpConnection?.uploadFolder ?? '',
            Protocol: Number(form.inboundDataFeeds.infinxIntegration.sftpConnection?.protocol),
            Port: Number(form.inboundDataFeeds.infinxIntegration.sftpConnection?.port),
            Timeout: Number(form.inboundDataFeeds.infinxIntegration.sftpConnection?.timeoutInSeconds),
        },
        SharedInvoiceProfileId: Number(form.inboundDataFeeds.infinxIntegration.invoiceProfileId),
    };

    return ftpInfinx;
}

function createMessageRecognitions(
    form: BuyerSignupFormData,
    baseViewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel>
): RecursivePartial<backendServices.ViewModels.MessageRecognitionViewModel>[] {
    // grab all message recognitions from the base view model
    const profileDeliveryMethod = baseViewModel.Profiles?.find(
        (profile) =>
            profile.Profile?.ProfileType === ProfileType.Invoice &&
            profile.Profile?.InvoiceProfileType !== InvoiceProfileInfinx
    );
    const messageRecognitions: RecursivePartial<backendServices.ViewModels.MessageRecognitionViewModel>[] = [];
    if (form.inboundDataFeeds.vendorFeedFormat !== ManualDataFeedFormat) {
        const messageRecognitionVendor = profileDeliveryMethod?.MessageRecognitions?.find(
            (mr) =>
                mr.MessageType === FeedMessageType.Vendor && mr.Id === Number(form.inboundDataFeeds.vendorFeedFormat)
        );
        if (messageRecognitionVendor) {
            messageRecognitions.push(messageRecognitionVendor);
        }
    }
    if (form.inboundDataFeeds.userFeedFormat !== ManualDataFeedFormat) {
        const messageRecognitionUser = profileDeliveryMethod?.MessageRecognitions?.find(
            (mr) => mr.MessageType === FeedMessageType.User && mr.Id === Number(form.inboundDataFeeds.userFeedFormat)
        );
        if (messageRecognitionUser) {
            messageRecognitions.push(messageRecognitionUser);
        }
    }
    if (form.inboundDataFeeds.glAccountFeedFormat !== ManualDataFeedFormat) {
        const messageRecognitionGLAccount = profileDeliveryMethod?.MessageRecognitions?.find(
            (mr) =>
                mr.MessageType === FeedMessageType.GLAccount &&
                mr.Id === Number(form.inboundDataFeeds.glAccountFeedFormat)
        );
        if (messageRecognitionGLAccount) {
            messageRecognitions.push(messageRecognitionGLAccount);
        }
    }
    if (form.inboundDataFeeds.poValidationFeedFormat !== ManualDataFeedFormat) {
        const messageRecognitionPO = profileDeliveryMethod?.MessageRecognitions?.find(
            (mr) => mr.MessageType === FeedMessageType.POUpload
        );
        if (messageRecognitionPO) {
            messageRecognitions.push(messageRecognitionPO);
        }
    }
    if (form.inboundDataFeeds.poDeliveryFeedFormat !== ManualDataFeedFormat) {
        const messageRecognitionPODelivery = profileDeliveryMethod?.MessageRecognitions?.find(
            (mr) => mr.MessageType === FeedMessageType.PODelivery
        );
        if (messageRecognitionPODelivery) {
            messageRecognitions.push(messageRecognitionPODelivery);
        }
    }

    return messageRecognitions;
}

function createFtpOutbound(
    form: BuyerSignupFormData
): RecursivePartial<backendServices.ViewModels.SharedFTPDownloadViewModel> {
    if (!form.outboundDataFeeds.deliveryMethod.useSftp) {
        return {};
    }

    const ftpOutbound = {
        FTPDownload: {
            Server: form.outboundDataFeeds.deliveryMethod.sftpConnection?.server ?? '',
            User: form.outboundDataFeeds.deliveryMethod.sftpConnection?.user ?? '',
            Password: form.outboundDataFeeds.deliveryMethod.sftpConnection?.password ?? '',
            DownloadFolder: form.outboundDataFeeds.deliveryMethod.sftpConnection?.downloadFolder ?? '',
            UploadFolder: form.outboundDataFeeds.deliveryMethod.sftpConnection?.uploadFolder ?? '',
            Protocol: Number(form.outboundDataFeeds.deliveryMethod.sftpConnection?.protocol),
            Port: Number(form.outboundDataFeeds.deliveryMethod.sftpConnection?.port),
            Timeout: Number(form.outboundDataFeeds.deliveryMethod.sftpConnection?.timeoutInSeconds),
        },
    };

    return ftpOutbound;
}

function createDeliveryMethodOutbound(
    form: BuyerSignupFormData,
    baseViewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel>
): RecursivePartial<backendServices.ViewModels.ProfileDeliveryMethodViewModel>[] {
    // set up delivery method file types
    const selectedFileTypesIdsOutbound = [
        form.outboundDataFeeds.fileTypes.usePDF && FileTypeId.PDF,
        form.outboundDataFeeds.fileTypes.useTIFF && FileTypeId.TIFF,
        form.outboundDataFeeds.fileTypes.useXML && FileTypeId.XML,
        form.outboundDataFeeds.fileTypes.useXML1 && FileTypeId.XML1,
        form.outboundDataFeeds.fileTypes.useOther && FileTypeId.Other,
    ].filter(Boolean);

    const defaultFileTypesOutbound =
        baseViewModel.Profiles?.find((profile) => profile.Profile?.ProfileType === ProfileType.BuyerDestination)
            ?.Profile?.ProfileDeliveryMethods?.[0]?.ProfileDeliveryFileTypes ?? [];

    const fileTypesOutbound = defaultFileTypesOutbound.filter((fileType) =>
        selectedFileTypesIdsOutbound.includes(fileType?.FileType)
    );

    if (form.outboundDataFeeds.fileTypes.useOther && form.outboundDataFeeds.fileTypes.fileTypeExtension) {
        const otherFileType = fileTypesOutbound.find((fileType) => fileType?.FileType === FileTypeId.Other);
        if (otherFileType) {
            otherFileType.FileSubType = form.outboundDataFeeds.fileTypes.fileTypeExtension;
        }
    }

    // determine delivery method ID
    const deliveryMethodIdOutbound = form.outboundDataFeeds.deliveryMethod.useSftp
        ? DeliveryMethod.FTP
        : form.outboundDataFeeds.deliveryMethod.useEmail
        ? DeliveryMethod.Email
        : form.outboundDataFeeds.deliveryMethod.useOracleSupplierNetwork
        ? DeliveryMethod.OracleSupplierNetwork
        : form.outboundDataFeeds.deliveryMethod.useTransceptaManager
        ? DeliveryMethod.TransceptaManager
        : form.outboundDataFeeds.deliveryMethod.useWebservice
        ? DeliveryMethod.WebService
        : DeliveryMethod.None;

    const deliveryMethodUriOutbound =
        form.outboundDataFeeds.deliveryMethod.uri ?? form.outboundDataFeeds.deliveryMethod.webserviceName ?? '';

    return [
        {
            DeliveryMethod: deliveryMethodIdOutbound,
            LastUpdateDate: new Date().toISOString(),
            IsEnabled: true,
            EmailAddress: form.outboundDataFeeds.deliveryMethod.emailAddress ?? '',
            Uri: deliveryMethodUriOutbound,
            ProfileDeliveryFileTypes: fileTypesOutbound,
            ProcessingMode: 1, // production processing mode
            GroupBy: deliveryMethodIdOutbound === DeliveryMethod.OracleSupplierNetwork ? 1 : 0, // if Oracle, no grouping (set to 1)
        },
    ];
}

// TODO: add support for Webservice settings?
function createOutboundFTPDeliveryMethodSettings(form: BuyerSignupFormData) {
    if (form.outboundDataFeeds.deliveryMethod.useSftp) {
        return {
            Server: form.outboundDataFeeds.deliveryMethod.sftpConnection?.server ?? '',
            User: form.outboundDataFeeds.deliveryMethod.sftpConnection?.user ?? '',
            Password: form.outboundDataFeeds.deliveryMethod.sftpConnection?.password ?? '',
            DownloadFolder: form.outboundDataFeeds.deliveryMethod.sftpConnection?.downloadFolder ?? '',
            UploadFolder: form.outboundDataFeeds.deliveryMethod.sftpConnection?.uploadFolder ?? '',
            Protocol: Number(form.outboundDataFeeds.deliveryMethod.sftpConnection?.protocol),
            Port: Number(form.outboundDataFeeds.deliveryMethod.sftpConnection?.port),
            Timeout: Number(form.outboundDataFeeds.deliveryMethod.sftpConnection?.timeoutInSeconds),
        };
    }

    return null;
}

function createBusinessDocEnables(form: BuyerSignupFormData): backendServices.ViewModels.BusinessDocEnableViewModel[] {
    const businessDocEnables: backendServices.ViewModels.BusinessDocEnableViewModel[] = [];
    if (form.buyerSettings.invoices.enable) {
        businessDocEnables.push({
            BusinessDocType: BusinessDocType.Invoice,
            BusinessDocType_US: 'Invoice',
            DocumentClasses: [],
        });
    }
    if (form.buyerSettings.poa.enable) {
        businessDocEnables.push({
            BusinessDocType: BusinessDocType.PurchaseOrderAcknowledgement,
            BusinessDocType_US: 'PO Acknowledgement',
            DocumentClasses: [],
        });
    }
    if (form.buyerSettings.asn.enable) {
        businessDocEnables.push({
            BusinessDocType: BusinessDocType.ASNGeneric,
            BusinessDocType_US: 'ASN Generic',
            DocumentClasses: [],
        });
    }
    if (form.buyerSettings.sim.enable) {
        businessDocEnables.push({
            BusinessDocType: BusinessDocType.SIMDocument,
            BusinessDocType_US: 'SIM',
            DocumentClasses: [],
        });
    }

    return businessDocEnables;
}

function createProfileExtendedData(
    form: BuyerSignupFormData,
    baseViewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel>
): RecursivePartial<backendServices.ViewModels.ProfileExtendedDataViewModel>[] {
    const currentDate = new Date().toISOString();
    let selectedProfileExtendedData = baseViewModel.Profiles?.find(
        (profileMessageRecognition) => profileMessageRecognition.Profile?.ProfileType === ProfileType.BuyerDestination
    )?.Profile?.ProfileExtendedDatas?.map((extendedData) => {
        const { ID, ProfileId, Rowversion, ...baseData } = extendedData; // remove unnecessary fields
        baseData.LastUpdateDate = currentDate;
        return baseData;
    });

    // if cloud code is disabled, remove GL Confidence Threshold and GL Auto Release Threshold
    if (!form.buyerSettings.workflow.cloudCode) {
        if (!form.buyerSettings.workflow.cloudCode) {
            selectedProfileExtendedData = selectedProfileExtendedData?.filter(
                (extendedData) =>
                    extendedData.Name?.trim() !== ProfileExtendedData.GLConfidenceThreshold &&
                    extendedData.Name?.trim() !== ProfileExtendedData.GLAutoReleaseThreshold
            );
        }
    }

    // PO and Non-PO
    if (!form.buyerSettings.workflow.referencePO) {
        const disablePOExtendedData = selectedProfileExtendedData?.find(
            (extendedData) => extendedData.Name?.trim() === ProfileExtendedData.DisablePO
        );
        if (disablePOExtendedData) {
            disablePOExtendedData.Value = '1';
        }
    }
    if (!form.buyerSettings.workflow.referenceNonPO) {
        const disableNonPOExtendedData = selectedProfileExtendedData?.find(
            (extendedData) => extendedData.Name?.trim() === ProfileExtendedData.DisableNonPO
        );
        if (disableNonPOExtendedData) {
            disableNonPOExtendedData.Value = '1';
        }
    }

    // PO Cloud Match
    if (!form.buyerSettings.invoices.poCloudMatching) {
        const disablePOCMExtendedData = selectedProfileExtendedData?.find(
            (extendedData) => extendedData.Name?.trim() === ProfileExtendedData.DisablePOCM
        );
        if (disablePOCMExtendedData) {
            disablePOCMExtendedData.Value = '1';
        }
    }
    if (form.buyerSettings.invoices.poCloudMatching && form.buyerSettings.invoices.poCloudMatchingForceOneLine) {
        const forceMatchOneLinePOExtendedData = selectedProfileExtendedData?.find(
            (extendedData) => extendedData.Name?.trim() === ProfileExtendedData.ForceMatchOneLinePO
        );
        if (forceMatchOneLinePOExtendedData) {
            forceMatchOneLinePOExtendedData.Value = '1';
        }
    }

    return selectedProfileExtendedData ?? [];
}

/**
 * Converts buyer signup form data to a buyer company view model (by modifying a base company) that can be sent to the backend.
 * Handles the following:
 * 1. General Information (company, user, and ERP information)
 * 2. Inbound Data Feeds (invoice and scan/key profiles)
 * 3. Outbound Data Feeds (buyer destination profile)
 * 4. Buyer Settings (enables, workflows, etc.)
 **/
export function convertFormToViewModel(
    form: BuyerSignupFormData,
    baseViewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel>
): RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel> {
    const { contacts: companyContacts, users } = createContactsAndUsers(form);

    // inbound FTP
    const ftpDeliveryMethod = createFTPInbound(form);
    const ftpInfinx = createFTPInfinx(form);

    // derive automated message recognitions
    const automatedMessageRecognitions = createMessageRecognitions(form, baseViewModel);

    // outbound delivery method data
    const profileDeliveryMethodsOutbound = createDeliveryMethodOutbound(form, baseViewModel);
    const ftpOutbound = createFtpOutbound(form);
    const profileDeliveryMethodSettingsOutbound = createOutboundFTPDeliveryMethodSettings(form);

    const businessDocEnables = createBusinessDocEnables(form);

    // data to be shared across all profiles
    const commonProfileData: RecursivePartial<backendServices.ViewModels.ProfileViewModel> = {
        Name: form.generalInformation.buyerCompanyName,
        Currency: form.generalInformation.currencyType,
        Address1: form.generalInformation.buyerCompanyAddress.addressLine1,
        Address2: form.generalInformation.buyerCompanyAddress.addressLine2,
        Address3: form.generalInformation.buyerCompanyAddress.addressLine3,
        City: form.generalInformation.buyerCompanyAddress.city,
        State: form.generalInformation.buyerCompanyAddress.state,
        PostalCode: form.generalInformation.buyerCompanyAddress.zipCode,
        Country: form.generalInformation.buyerCompanyAddress.country,
        Phone: form.generalInformation.buyerCompanyAddress.phone,
        Email: companyContacts[0].Email,
        InvoiceConfirmationEmail: companyContacts[0].Email,
        RemittanceAdviceEmail: companyContacts[0].Email,
        // empty defaults
        Signature: '',
        CountryOfEstablishment: '',
    };

    // adjust scan/key message recognitions based on buyer settings
    let scanKeyMessageRecognitions = baseViewModel.Profiles?.find(
        (profileMessageRecognition) =>
            profileMessageRecognition.Profile?.ProfileType === ProfileType.Invoice &&
            profileMessageRecognition.Profile?.InvoiceProfileType === InvoiceProfileInfinx
    )?.MessageRecognitions;
    if (form.buyerSettings.nextGenSupplier.ocr) {
        scanKeyMessageRecognitions = scanKeyMessageRecognitions?.map((messageRecognition) => {
            if (messageRecognition.MessageType === MessageType.ScanKeySourceFileEmailForward) {
                return {
                    ...messageRecognition,
                    Type: MessageType.ScanKeySourceFileEmailForward_OCR,
                };
            }
            return messageRecognition;
        });
    }

    // adjust profile extended data based on buyer settings
    const selectedProfileExtendedData = createProfileExtendedData(form, baseViewModel);

    const finalViewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel> = {
        // 1. General Information (populate company, user, and ERP information)
        Company: {
            ID: undefined,
            Name: form.generalInformation.buyerCompanyName,
            Address1: form.generalInformation.buyerCompanyAddress.addressLine1,
            Address2: form.generalInformation.buyerCompanyAddress.addressLine2,
            Address3: form.generalInformation.buyerCompanyAddress.addressLine3,
            City: form.generalInformation.buyerCompanyAddress.city,
            State: form.generalInformation.buyerCompanyAddress.state,
            PostalCode: form.generalInformation.buyerCompanyAddress.zipCode,
            Country: form.generalInformation.buyerCompanyAddress.country,
            Contacts: companyContacts,
            Email: companyContacts[0].Email,
            IDEAutoExtractThreshold: form.buyerSettings.nextGenSupplier.ideTemplateMatching
                ? Number(form.buyerSettings.nextGenSupplier.threshold)
                : IDE_THRESHOLD_MAX,
            EnablePOFlip: form.buyerSettings.invoices.poFlip ? 1 : 0,
            Phone: form.generalInformation.buyerCompanyAddress.phone,
            ParkingEnabled: false,
            EnablePODelivery: form.buyerSettings.poForDelivery.enable,
        },
        Users: users,
        BuyerERPConnection: {
            ERPId: form.generalInformation.erpSystemId,
        },
        Profiles: baseViewModel.Profiles?.map((profileMessageRecognition) => {
            const updatedProfile = {
                ...profileMessageRecognition,
                Profile: {
                    ...profileMessageRecognition.Profile,
                    ...commonProfileData,
                },
            };

            // 2. Inbound Data Feeds (invoice and scan/key profiles)
            if (
                profileMessageRecognition.Profile?.ProfileType === ProfileType.Invoice &&
                profileMessageRecognition.Profile?.InvoiceProfileType !== InvoiceProfileInfinx
            ) {
                return {
                    Profile: {
                        ...updatedProfile.Profile,
                        ProfileType: ProfileType.Invoice,
                    },
                    MessageRecognitions: automatedMessageRecognitions,
                    FTPDownloads: ftpDeliveryMethod,
                };
            }
            if (
                profileMessageRecognition.Profile?.ProfileType === ProfileType.Invoice &&
                profileMessageRecognition.Profile?.InvoiceProfileType === InvoiceProfileInfinx
            ) {
                return {
                    Profile: {
                        ...updatedProfile.Profile,
                        ProfileType: ProfileType.Invoice,
                        InvoiceProfileType: InvoiceProfileInfinx,
                        ScanKeyProfileId: Number(form.inboundDataFeeds.infinxIntegration.invoiceProfileId ?? null),
                    },
                    MessageRecognitions: scanKeyMessageRecognitions,
                    FTPDownloads: ftpInfinx,
                };
            }

            // 3. Outbound Data Feeds (buyer destination profile)
            if (profileMessageRecognition.Profile?.ProfileType === ProfileType.BuyerDestination) {
                return {
                    Profile: {
                        ...updatedProfile.Profile,
                        ProfileType: ProfileType.BuyerDestination,
                        ProfileDeliveryMethods: profileDeliveryMethodsOutbound,
                        ProfileExtendedDatas: selectedProfileExtendedData, // from Step 4
                    },
                    FTPDownloads: ftpOutbound,
                    FTPDeliveryMethodSettings: profileDeliveryMethodSettingsOutbound,
                };
            }

            // return unchanged profile for all other cases
            return updatedProfile;
        }),
        // 4. Buyer Settings (enables, workflows, etc.)
        BusinessDocEnables: businessDocEnables,
        Workflows: {
            EnableParkingLotWorkflow: false,
        },
    };

    // remove infinx/ScanKey profile if user opts out
    if (!form.inboundDataFeeds.infinxIntegration.useInfinxIntegration) {
        finalViewModel.Profiles = finalViewModel.Profiles?.filter(
            (profileMessageRecognition) =>
                !(
                    profileMessageRecognition.Profile?.ProfileType === ProfileType.Invoice &&
                    profileMessageRecognition.Profile?.InvoiceProfileType === InvoiceProfileInfinx
                )
        );
    }

    return finalViewModel;
}
